import { motion } from 'framer-motion'
import {
  DevicePhoneMobileIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { Card } from '../components/ui/Card'

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

const features = [
  { title: 'iOS & Android apps', desc: 'Native performance on both platforms from a single codebase.' },
  { title: 'Cross-platform development', desc: 'Build once, deploy everywhere with React Native or Flutter.' },
  { title: 'App Store submission', desc: 'End-to-end submission handling for Apple App Store and Google Play.' },
  { title: 'Push notifications', desc: 'Engage users with targeted push notification campaigns.' },
]

const providers = [
  { name: 'React Native', url: 'https://reactnative.dev', desc: 'Meta\'s cross-platform framework' },
  { name: 'Flutter', url: 'https://flutter.dev', desc: 'Google\'s UI toolkit for apps' },
  { name: 'Expo', url: 'https://expo.dev', desc: 'Platform for React Native apps' },
  { name: 'Firebase', url: 'https://firebase.google.com', desc: 'Backend services for apps' },
]

export const MobileAppPage = () => {
  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
      <motion.div variants={item}>
        <h1 className="text-2xl font-bold text-metallic-900">Mobile App Development</h1>
        <p className="text-metallic-500 mt-1">Cross-platform mobile app development for iOS and Android.</p>
      </motion.div>

      <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {features.map((f) => (
          <Card key={f.title} hover>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <DevicePhoneMobileIcon className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-metallic-900">{f.title}</h3>
                <p className="text-sm text-metallic-500 mt-1">{f.desc}</p>
              </div>
            </div>
          </Card>
        ))}
      </motion.div>

      <motion.div variants={item}>
        <Card>
          <h3 className="text-lg font-semibold text-metallic-900 mb-4">Service Providers</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {providers.map((p) => (
              <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between p-4 rounded-xl border border-metallic-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all group">
                <div>
                  <p className="font-medium text-metallic-900 group-hover:text-indigo-600">{p.name}</p>
                  <p className="text-sm text-metallic-500">{p.desc}</p>
                </div>
                <ArrowTopRightOnSquareIcon className="w-5 h-5 text-metallic-400 group-hover:text-indigo-500" />
              </a>
            ))}
          </div>
        </Card>
      </motion.div>

      <motion.div variants={item}>
        <Card className="bg-gradient-to-r from-indigo-50 to-white border-indigo-200">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircleIcon className="w-6 h-6 text-indigo-600" />
            <h3 className="font-semibold text-metallic-900">What's Included</h3>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {['UI/UX design', 'API integration', 'Offline support', 'In-app purchases', 'Analytics dashboard', 'Post-launch support'].map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-metallic-600">
                <CheckCircleIcon className="w-4 h-4 text-success-500 flex-shrink-0" />{f}
              </li>
            ))}
          </ul>
        </Card>
      </motion.div>
    </motion.div>
  )
}
